import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { StudioService } from '../../core/services/studio.service';
import { StudioSelectionService } from '../../core/services/studio-selection.service';
import { environment } from '../../../environments/environment';
import Chart from 'chart.js';

const SESSION_KEY = 'marketing_select_studio_id';

function buildYearOptions() {
  const now = new JstDate();
  const opts = [];
  for (let y = now.getFullYear() + 1; y >= 2015; y--) {
    opts.push({ id: y, name: `${y}年` });
  }
  return opts;
}
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ id: i + 1, name: `${i + 1}月` }));

const PALETTES = ['#fff000', '#4ebcc4', '#51adcf', '#0278ae'];
const COMPOSITE_PALETTES = ['#E9516F', '#fff000', '#51adcf', '#0278ae', '#70AD47', '#4ebcc4'];

function createGraphData(data: any): any {
  if (!data?.label || !data?.data) return null;
  const datasets = Array.isArray(data.data) ? data.data : [];
  datasets.forEach((v: any, i: number) => {
    v.borderColor = PALETTES[i] ?? '#4ebcc4';
    v.backgroundColor = PALETTES[i] ?? '#4ebcc4';
    v.lineTension = 0;
  });
  return {
    type: 'bar',
    data: { labels: data.label, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: { yAxes: [{ type: 'linear', ticks: { min: 0 } }] },
    },
  };
}

function createLineGraphData(data: any): any {
  const config = createGraphData(data);
  if (!config) return null;
  config.type = 'line';
  if (config.data.datasets[0]) config.data.datasets[0].fill = false;
  return config;
}

function createCompositeGraphData(data: any): any {
  if (!data?.label || !data?.data) return null;
  const datasets = Array.isArray(data.data) ? data.data : [];
  const allValues = datasets.reduce((a: any[], v: any) => a.concat(v.data ?? []), []);
  const suggestedMin = allValues.length ? Math.min(...allValues) : 0;
  const suggestedMax = allValues.length ? Math.max(...allValues) : 100;
  datasets.forEach((v: any, i: number) => {
    v.borderColor = COMPOSITE_PALETTES[i] ?? '#4ebcc4';
    v.backgroundColor = COMPOSITE_PALETTES[i] ?? '#4ebcc4';
    v.lineTension = 0;
    if (i === datasets.length - 1) {
      v.yAxisID = 'y-axis-2';
    } else {
      v.yAxisID = 'y-axis-1';
      v.fill = false;
      v.type = 'line';
    }
  });
  return {
    type: 'bar',
    data: { labels: data.label, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          { id: 'y-axis-1', type: 'linear', position: 'left', ticks: { suggestedMin, suggestedMax } },
          { id: 'y-axis-2', type: 'linear', position: 'right', ticks: { suggestedMin, suggestedMax }, gridLines: { drawOnChartArea: false } },
        ],
      },
    },
  };
}

@Component({
    selector: 'app-marketing-studio',
    imports: [FormsModule, DecimalPipe],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-list"></i> 一覧</span>
    </div>
    <div class="card-body">
      <form novalidate name="myForm" #myForm="ngForm">
      <div class="row">
        <div class="col-md-4">
          <div class="form-group row">
            <div class="col-md-12">
              <label class="col-form-label" for="studio_name">店舗</label>
              <select class="form-control" id="studio_name" name="studio_name" [(ngModel)]="selectedStudio" (ngModelChange)="onStudioChange()" [disabled]="!!selectedStudio && !isSetExport">
                <option [ngValue]="null">-- 選択してください --</option>
                @for (s of studios; track s.id) {
                  <option [ngValue]="s">{{ s.name }}</option>
                }
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group row">
        <div class="col-md-12">
          <button class="btn btn-success text-white" type="button" (click)="download()" [disabled]="!selectedStudio || !isDlButtonActive || !isSetExport">
            <i class="fa s-icon-dawnload"></i>&nbsp;ダウンロード
          </button>
        </div>
      </div>
      </form>
      <hr>
    </div>
    <div class="card-body">
      <form name="marketingStudioForm" method="POST" [attr.action]="marketingStudioAction" style="display: none;">
        <input type="text" name="marketingStudio">
        <input type="text" name="selectYear">
        <input type="text" name="selectStudioName" class="">
      </form>

      @if (searching) {
        <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
      }

      <div class="mb-5">
        <strong>{{ targetMonthStr }}月末会員数</strong>
        <hr />
        @if (!dataNumberOfMembers) {
          <div class="mb-4">該当するデータはありません</div>
        } @else {
          <div class="col-md-12">
            <label class="col-form-label">合計: {{ dataNumberOfMembers.data?.total | number }}人</label>
          </div>
          <div class="col-md-12">
            <label class="col-form-label">坪あたり会員数: {{ numericValue(dataNumberOfMembers.data?.perArea) | number:'1.1-1' }}人/坪&nbsp;&nbsp;{{ dataNumberOfMembers.data?.rank }}位/{{ dataNumberOfMembers.data?.totalStudios }}店舗</label>
          </div>
        }
      </div>

      <div class="mb-5">
        <strong>{{ targetMonthStr }}月入会者数</strong>
        <hr />
        @if (!dataEnable) {
          <div class="mb-4">該当するデータはありません</div>
        } @else {
          <div class="col-md-10">
            <label class="col-form-label">合計: {{ dataEnable.data?.total | number }}人&nbsp;&nbsp;{{ dataEnable.data?.rank }}位/{{ dataEnable.data?.totalStudios }}店舗</label>
            <div class="w-100 text-right"><small>単位: 人</small></div>
            <table class="table table-bordered table-sm table-hover table-ptb-0">
              <thead>
                <tr>
                  <th>24時間コース</th>
                  <th>デイコース</th>
                  <th>モーニング･スチューデントコース</th>
                  <th>休会</th>
                  <th>その他</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-right">{{ dataEnable.data?.full | number }}</td>
                  <td class="text-right">{{ dataEnable.data?.day | number }}</td>
                  <td class="text-right">{{ dataEnable.data?.student | number }}</td>
                  <td class="text-right">{{ dataEnable.data?.suspend | number }}</td>
                  <td class="text-right">{{ dataEnable.data?.other | number }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mb-4 canvas-container" [class.d-none]="!dataEnable.graphData" style="height: 300px;">
            <canvas id="myChartEnable"></canvas>
          </div>
        }
      </div>

      <div class="mb-5">
        <strong>{{ targetMonthStr }}月退会者数</strong>
        <hr />
        @if (!dataDisable) {
          <div class="mb-4">該当するデータはありません</div>
        } @else {
          <div class="col-md-10">
            <label class="col-form-label">合計: {{ dataDisable.data?.total | number }}人&nbsp;&nbsp;{{ dataDisable.data?.rank }}位/{{ dataDisable.data?.totalStudios }}店舗</label>
            <div class="w-100 text-right"><small>単位: 人</small></div>
            <table class="table table-bordered table-sm table-hover table-ptb-0">
              <thead>
                <tr>
                  <th>24時間コース</th>
                  <th>デイコース</th>
                  <th>モーニング･スチューデントコース</th>
                  <th>休会</th>
                  <th>その他</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-right">{{ dataDisable.data?.full | number }}</td>
                  <td class="text-right">{{ dataDisable.data?.day | number }}</td>
                  <td class="text-right">{{ dataDisable.data?.student | number }}</td>
                  <td class="text-right">{{ dataDisable.data?.suspend | number }}</td>
                  <td class="text-right">{{ dataDisable.data?.other | number }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mb-4 canvas-container" [class.d-none]="!dataDisable.graphData" style="height: 300px;">
            <canvas id="myChartDisable"></canvas>
          </div>
        }
      </div>

      <div class="mb-5">
        <strong>{{ targetMonthStr }}月純増会員数</strong>
        <hr />
        @if (!dataIncrease) {
          <div class="mb-4">該当するデータはありません</div>
        } @else {
          <div class="col-md-10">
            <label class="col-form-label">合計: {{ dataIncrease.data?.total | number }}人&nbsp;&nbsp;{{ dataIncrease.data?.rank }}位/{{ dataIncrease.data?.totalStudios }}店舗</label>
            <div class="w-100 text-right"><small>単位: 人</small></div>
            <table class="table table-bordered table-sm table-hover table-ptb-0">
              <thead>
                <tr>
                  <th>24時間コース</th>
                  <th>デイコース</th>
                  <th>モーニング･スチューデントコース</th>
                  <th>休会</th>
                  <th>その他</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-right">{{ dataIncrease.data?.full | number }}</td>
                  <td class="text-right">{{ dataIncrease.data?.day | number }}</td>
                  <td class="text-right">{{ dataIncrease.data?.student | number }}</td>
                  <td class="text-right">{{ dataIncrease.data?.suspend | number }}</td>
                  <td class="text-right">{{ dataIncrease.data?.other | number }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mb-4 canvas-container" [class.d-none]="!dataIncrease.graphData" style="height: 300px;">
            <canvas id="myChartIncrease"></canvas>
          </div>
        }
      </div>

      <div class="mb-5">
        <strong>会員数推移</strong>
        <hr />
        @if (!dataNumberOfMembers?.graphData) {
          <div class="mb-4">該当するデータはありません</div>
        } @else {
          <div class="mb-4 canvas-container" style="height: 300px;">
            <canvas id="myChartNumberOfMembers"></canvas>
          </div>
        }
      </div>

      <div class="mb-5">
        <strong>来館者分析</strong>
        <hr />
        @if (!dataVisitor) {
          <div class="mb-4">該当するデータはありません</div>
        } @else {
          <div class="mb-4 canvas-container" [class.d-none]="!dataVisitor.graphData" style="height: 300px;">
            <canvas id="myChartVisitor"></canvas>
          </div>
        }
      </div>

      <div class="mb-5">
        <strong>PT実施本数</strong>
        <hr />
        @if (!dataPT) {
          <div class="mb-4">該当するデータはありません</div>
        } @else {
          <div class="mb-4 canvas-container" [class.d-none]="!dataPT.graphData" style="height: 300px;">
            <canvas id="myChartPT"></canvas>
          </div>
        }
      </div>
    </div>
  </div>
</div>
  `
})
export class MarketingStudioComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private studioService = inject(StudioService);
  private studioSelection = inject(StudioSelectionService);

  studios: any[] = [];
  selectedStudio: any = null;
  dataNumberOfMembers: any = null;
  dataEnable: any = null;
  dataDisable: any = null;
  dataIncrease: any = null;
  dataVisitor: any = null;
  dataPT: any = null;
  searching = false;
  isSetExport = true;
  isDlButtonActive = true;
  private charts: Record<string, any> = {};

  readonly marketingStudioAction = `${environment.apiBaseUrl}/marketing/excel/marketing_studio`;

  get targetMonthStr(): string {
    const now = new JstDate();
    const prev = new JstDate(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  }

  numericValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('マーケティング', 'fa fa-area-chart');
    this.studios = await this.studioService.list('all') ?? [];
    const restored = this.studioSelection.restore(this.studios, SESSION_KEY);
    if (restored) {
      this.selectedStudio = restored;
      await this.load();
    }
  }

  onStudioChange(): void {
    this.clearData();
    if (!this.selectedStudio) { this.studioSelection.clear(SESSION_KEY); return; }
    this.studioSelection.save(this.selectedStudio, SESSION_KEY);
    this.load();
  }

  clearData(): void {
    this.destroyAllCharts();
    this.dataNumberOfMembers = null;
    this.dataEnable = null;
    this.dataDisable = null;
    this.dataIncrease = null;
    this.dataVisitor = null;
    this.dataPT = null;
  }

  private destroyAllCharts(): void {
    Object.values(this.charts).forEach(c => { if (c) c.destroy(); });
    this.charts = {};
  }

  private renderChart(id: string, config: any): void {
    if (!config) return;
    const ctx = (document.getElementById(id) as HTMLCanvasElement)?.getContext('2d');
    if (!ctx) return;
    ctx.canvas.height = 300;
    if (this.charts[id]) { this.charts[id].destroy(); }
    this.charts[id] = new Chart(ctx, config);
  }

  download(): void {
    if (!this.selectedStudio) return;
    this.isDlButtonActive = false;
    const itemsData: any[] = [];
    const collectXlsx = (data: any, graphName: string, dataName: string) => {
      if (!data) return;
      itemsData.push(...this.createXlsxData(data, graphName, dataName));
    };
    collectXlsx(this.dataPT, 'PT実施本数', '');
    collectXlsx(this.dataVisitor, '来館者分析', '');
    collectXlsx(this.dataNumberOfMembers, '会員数推移', '坪あたり会員数/順位');
    collectXlsx(this.dataIncrease, '純増会員数', '純増会員数/順位');
    collectXlsx(this.dataDisable, '退会者数', '退会者数/順位');
    collectXlsx(this.dataEnable, '入会者数', '入会者数/順位');
    const xlabels = this.dataEnable?.graphData?.label ?? [];
    const form = document.forms.namedItem('marketingStudioForm') as HTMLFormElement | null;
    if (!form) {
      this.isDlButtonActive = true;
      return;
    }
    form.action = this.marketingStudioAction;
    (form.elements.namedItem('marketingStudio') as HTMLInputElement).value = JSON.stringify([{ data: itemsData, labels: xlabels }]);
    (form.elements.namedItem('selectYear') as HTMLInputElement).value = this.targetMonthStr;
    (form.elements.namedItem('selectStudioName') as HTMLInputElement).value = this.selectedStudio.name;
    form.submit();
    window.setTimeout(() => { this.isDlButtonActive = true; }, 5000);
  }

  private createXlsxData(data: any, graphDataName: string, dataName: string): any[] {
    const json: any[] = [];
    const targetMonth = this.targetMonthStr;
    if (data.data) {
      let total: string;
      if (data.data.perArea !== undefined) {
        total = (data.data.perArea !== '-'
          ? String(Math.round(parseFloat(data.data.perArea) * 100) / 100)
          : data.data.perArea) + `人/坪 ${data.data.rank}位/${data.data.totalStudios}店舗`;
      } else {
        total = `${data.data.total}${dataName === '販促実施状況/順位' ? '円 ' : '人 '}${data.data.rank}位/${data.data.totalStudios}店舗`;
      }
      json.push({ studio: this.selectedStudio?.name, label: dataName, val11: total });
    }
    if (!data.graphData) return json;
    if (graphDataName === '来館者分析') {
      (data.graphData.data[0].data as any[]).forEach((v: any, i: number) => {
        const row: any = { studio: this.selectedStudio?.name, label: `${graphDataName}/${data.graphData.label[i]}` };
        row['val11'] = v;
        json.push(row);
      });
    } else {
      const lastSplit = targetMonth.split('-');
      const startingMonth = Number(lastSplit[1]) === 12 ? 1 : Number(lastSplit[1]) + 1;
      const indexArr: number[] = (data.graphData.label as string[]).map((b: string) => {
        const m = Number(b.split('-')[1]);
        return m - startingMonth < 0 ? 12 + (m - startingMonth) : m - startingMonth;
      });
      (data.graphData.data as any[]).forEach((v: any) => {
        const row: any = { studio: this.selectedStudio?.name, label: graphDataName ? `${graphDataName}/${v.label}` : v.label };
        (v.data as any[]).forEach((w: any, i: number) => { row[`val${indexArr[i]}`] = w; });
        json.push(row);
      });
    }
    return json;
  }

  async load(): Promise<void> {
    if (!this.selectedStudio) return;
    this.searching = true;
    this.isSetExport = false;
    this.clearData();
    const targetMonth = this.targetMonthStr;
    try {
      const [promotion, pt, visitor, numberOfMembers, increase, disable, enable] = await Promise.all([
        this.studioService.getPromotion(this.selectedStudio.id, targetMonth),
        this.studioService.getPT(this.selectedStudio.id, targetMonth),
        this.studioService.getVisitor(this.selectedStudio.id, targetMonth),
        this.studioService.getNumberOfMembers(this.selectedStudio.id, targetMonth),
        this.studioService.getIncrease(this.selectedStudio.id, targetMonth),
        this.studioService.getDisable(this.selectedStudio.id, targetMonth),
        this.studioService.getEnable(this.selectedStudio.id, targetMonth),
      ]);
      this.dataNumberOfMembers = numberOfMembers || null;
      this.dataEnable = enable || null;
      this.dataDisable = disable || null;
      this.dataIncrease = increase || null;
      this.dataVisitor = visitor || null;
      this.dataPT = pt || null;
      setTimeout(() => this.renderAllCharts(), 0);
    } finally {
      this.isSetExport = true;
      this.searching = false;
    }
  }

  private renderAllCharts(): void {
    if (this.dataEnable?.graphData) {
      this.renderChart('myChartEnable', createCompositeGraphData(this.dataEnable.graphData));
    }
    if (this.dataDisable?.graphData) {
      this.renderChart('myChartDisable', createCompositeGraphData(this.dataDisable.graphData));
    }
    if (this.dataIncrease?.graphData) {
      this.renderChart('myChartIncrease', createCompositeGraphData(this.dataIncrease.graphData));
    }
    if (this.dataNumberOfMembers?.graphData) {
      this.renderChart('myChartNumberOfMembers', createLineGraphData(this.dataNumberOfMembers.graphData));
    }
    if (this.dataVisitor?.graphData) {
      this.renderChart('myChartVisitor', createGraphData(this.dataVisitor.graphData));
    }
    if (this.dataPT?.graphData) {
      this.renderChart('myChartPT', createGraphData(this.dataPT.graphData));
    }
  }
}
